'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

// ─── Video data ────────────────────────────────────────────────────────────────
const SHOWREEL = {
  title: 'Showreel 2026',
  tag: 'Featured',
  youtubeId: 'HAj3n4Ae5wA',
  description:
    'A fast paced showreel, showcasing my editing skills.',
};

const LONGFORM = {
  title: 'Long Form Editing',
  tag: 'Documentary',
  youtubeId: 'o3nlAWMTtE4',
  playlistId: 'PLVSJNCz8aI9MVUa3EA2XNTetj9uFzlmh8',
  description:
    'Full-length documentary and narrative editing — pacing, story structure.',
};

const SHORTS = [
  {
    title: 'Short Form #1',
    tag: 'TikTok',
    youtubeId: 'EjxM83GoqFw',
    description: 'Fast-cut branded short-form content',
  },
  {
    title: 'Short Form #2',
    tag: 'Reel',
    youtubeId: 'qvlTvygecPo',
    description: 'Marketing TikTok',
  },
  {
    title: 'Short Form #3',
    tag: 'Shorts',
    youtubeId: 'cikF1TEYpiM',
    description: 'Dynamic event highlight reel',
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
const Homepage: React.FC = () => {
  const galleryRef = useRef<HTMLDivElement>(null);

  const scrollToGallery = () => {
    galleryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const elements = document.querySelectorAll('.reveal-up');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        background: '#0f0f0f',
        minHeight: '100vh',
        fontFamily: 'DM Sans, sans-serif',
        position: 'relative',
      }}
    >
      {/* Noise texture overlay */}
      <div className="noise-overlay" aria-hidden="true" />

      {/* Ambient background blobs */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: '10%',
          left: '5%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(220,38,38,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
          filter: 'blur(60px)',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          bottom: '20%',
          right: '5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(220,38,38,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
          filter: 'blur(80px)',
        }}
      />

      <Header />

      {/* ── HERO SECTION ──────────────────────────────────────────────────── */}
      <section
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 2,
          padding: '100px 24px 60px',
        }}
      >
        {/* Status badge */}
        <div
          className="reveal-up"
          style={{ marginBottom: '48px', textAlign: 'center' }}
        >
          <span className="tag-pill">
            <span className="accent-dot" />
            Available for Work
          </span>
        </div>

        {/* Main hero links */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            textAlign: 'center',
          }}
        >
          <div className="reveal-up delay-1">
            <button
              onClick={scrollToGallery}
              className="hero-nav-link"
              style={{
                background: 'none',
                border: 'none',
                padding: '4px 0',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: 'clamp(48px, 8vw, 96px)',
                fontWeight: 700,
                color: '#f5f5f5',
                letterSpacing: '-0.03em',
                cursor: 'pointer',
                display: 'block',
                position: 'relative',
              }}
              aria-label="Scroll to work gallery"
            >
              View Work
              <span
                style={{
                  position: 'absolute',
                  bottom: '-4px',
                  left: 0,
                  width: 0,
                  height: '3px',
                  background: '#dc2626',
                  borderRadius: '2px',
                  transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                className="underline-bar"
              />
            </button>
          </div>

          {/* Divider */}
          <div
            className="reveal-up delay-2"
            style={{
              width: '1px',
              height: '32px',
              background:
                'linear-gradient(to bottom, transparent, rgba(220,38,38,0.4), transparent)',
              margin: '4px auto',
            }}
          />

          <div className="reveal-up delay-3">
            <Link
              href="/contact"
              className="hero-nav-link"
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: 'clamp(48px, 8vw, 96px)',
                fontWeight: 700,
                letterSpacing: '-0.03em',
                color: '#6b7280',
                textDecoration: 'none',
                display: 'block',
                padding: '4px 0',
                transition: 'color 0.3s ease',
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.color = '#fca5a5')
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.color = '#6b7280')
              }
            >
              Contact Me
            </Link>
          </div>
        </div>

        {/* Subtitle */}
        <p
          className="reveal-up delay-4"
          style={{
            marginTop: '48px',
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '16px',
            fontWeight: 400,
            color: '#4b5563',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          Video Editor · Motion Designer · Storyteller
        </p>

        {/* Scroll indicator */}
        <button
          onClick={scrollToGallery}
          className="reveal-up delay-5"
          aria-label="Scroll down"
          style={{
            marginTop: '64px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            opacity: 0.5,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLElement).style.opacity = '1')
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLElement).style.opacity = '0.5')
          }
        >
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#6b7280',
            }}
          >
            Scroll
          </span>
          <svg
            width="16"
            height="24"
            viewBox="0 0 16 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="0.5"
              y="0.5"
              width="15"
              height="23"
              rx="7.5"
              stroke="#4b5563"
              strokeWidth="1"
            />
            <rect
              x="7"
              y="4"
              width="2"
              height="6"
              rx="1"
              fill="#dc2626"
              style={{
                animation: 'scrollDot 1.8s ease-in-out infinite',
              }}
            />
          </svg>
          <style>{`
            @keyframes scrollDot {
              0% { transform: translateY(0); opacity: 1; }
              100% { transform: translateY(8px); opacity: 0; }
            }
          `}</style>
        </button>
      </section>

      {/* ── WORK GALLERY ──────────────────────────────────────────────────── */}
      <section
        ref={galleryRef}
        id="work"
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '80px 24px 120px',
          maxWidth: '1280px',
          margin: '0 auto',
        }}
      >
        {/* Section header */}
        <div
          className="reveal-up"
          style={{ marginBottom: '48px' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px',
            }}
          >
            <span className="tag-pill">Work Gallery</span>
            <div
              style={{
                height: '1px',
                flex: 1,
                background:
                  'linear-gradient(to right, rgba(220,38,38,0.3), transparent)',
              }}
            />
          </div>
          <h2
            style={{
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: '#f5f5f5',
              margin: 0,
            }}
          >
            Selected Projects
          </h2>
        </div>

        {/* ── BENTO GRID ─────────────────────────────────────────────────── */}
        <div className="bento-grid">

          {/* TOP — Showreel (full width) */}
          <div
            className="bento-showreel glass-card reveal-up"
            style={{ padding: '24px' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span className="tag-pill">
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#f59e0b',
                      display: 'inline-block',
                    }}
                  />
                  {SHOWREEL.tag}
                </span>
                <h3
                  style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontWeight: 700,
                    fontSize: '22px',
                    color: '#f5f5f5',
                    margin: 0,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {SHOWREEL.title}
                </h3>
              </div>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: 0,
                  maxWidth: '400px',
                }}
              >
                {SHOWREEL.description}
              </p>
            </div>
            <div className="video-embed">
              <iframe
                src={`https://www.youtube.com/embed/${SHOWREEL.youtubeId}?rel=0&modestbranding=1`}
                title={SHOWREEL.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* MIDDLE — Long Form (spans 2 cols) + stat card */}
          <div
            className="bento-longform glass-card reveal-up delay-1"
            style={{ padding: '24px' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
              }}
            >
              <span className="tag-pill">{LONGFORM.tag}</span>
              <h3
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#f5f5f5',
                  margin: 0,
                  letterSpacing: '-0.02em',
                }}
              >
                {LONGFORM.title}
              </h3>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '13px',
                  color: '#6b7280',
                  margin: 0,
                }}
              >
                {LONGFORM.description}
              </p>
            </div>
            <div className="video-embed">
              <iframe
                src={`https://www.youtube.com/embed/${LONGFORM.youtubeId}?rel=0&modestbranding=1&list=${LONGFORM.playlistId}`}
                title={LONGFORM.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>

          {/* Stat card alongside long form (1 col) */}
          <div
            className="glass-card reveal-up delay-2"
            style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '24px',
              gridColumn: 'span 1',
            }}
          >
            <div>
              <span className="tag-pill" style={{ marginBottom: '20px', display: 'inline-flex' }}>
                At a Glance
              </span>
            </div>

            {[
              { value: '50+', label: 'Videos Delivered' },
              { value: '4.9★', label: 'Client Rating' },
              { value: '48hr', label: 'Avg. Turnaround' },
              { value: '6 months', label: 'Experience' },
            ].map((stat, i) => (
              <div
                key={i}
                style={{
                  borderBottom:
                    i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                  paddingBottom: i < 3 ? '16px' : 0,
                }}
              >
                <div
                  style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '32px',
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: '#f5f5f5',
                    lineHeight: 1,
                  }}
                >
                  {stat.value}
                </div>
                <div
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: '#6b7280',
                    marginTop: '4px',
                  }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          {/* BOTTOM — Three Shorts */}
          {SHORTS.map((short, i) => (
            <div
              key={i}
              className={`bento-short glass-card reveal-up delay-${i + 2}`}
              style={{ padding: '20px' }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '16px',
                }}
              >
                <span className="tag-pill">{short.tag}</span>
                <h3
                  style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#f5f5f5',
                    margin: 0,
                  }}
                >
                  {short.title}
                </h3>
              </div>
              <div className="video-embed-short">
                <iframe
                  src={`https://www.youtube.com/embed/${short.youtubeId}?rel=0&modestbranding=1`}
                  title={short.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '13px',
                  color: '#6b7280',
                  margin: '12px 0 0',
                }}
              >
                {short.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA below gallery */}
        <div
          className="reveal-up"
          style={{
            marginTop: '64px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Link
            href="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '16px 36px',
              background: 'rgba(220, 38, 38, 0.9)',
              border: '1px solid rgba(220, 38, 38, 0.5)',
              borderRadius: '14px',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              color: '#f5f5f5',
              textDecoration: 'none',
              transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(220, 38, 38, 1)';
              (e.currentTarget as HTMLElement).style.boxShadow = '0 0 40px rgba(220, 38, 38, 0.35)';
              (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(220, 38, 38, 0.9)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
            }}
          >
            Let's Work Together
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Homepage;